import {
  Alert,
  Card,
  FormField,
  Separator,
  type FormControl,
  Switch,
  Table,
  TabsHeader,
  type TabIconVariant,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import type {UseFormReturn} from 'react-hook-form';

import {useGetDefinitionTemplateListQuery, useGetSeriesListQuery} from '@dms/api/core';
import {
  copyCheckoutProfileRegime,
  getCheckoutProfileDocumentCells,
  getCheckoutProfileDocumentCellKey,
  getCheckoutProfileDocumentTypes,
  normalizeCheckoutProfileDraft,
  type SaleRegime,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import type {CheckoutProfileIssue} from '../../../utils/getCheckoutProfileIssueMessage';
import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';
import {checkoutProfileOptions} from '../utils/checkoutProfileOptions';
import {getCheckoutProfileTemplateOptions} from '../utils/getCheckoutProfileTemplateOptions';
import {CheckoutProfileDocumentCopy} from './CheckoutProfileDocumentCopy';
import {CheckoutProfileDocumentRow} from './CheckoutProfileDocumentRow';
import {CheckoutProfileFeedback} from './CheckoutProfileFeedback';

const documentTypeOrder = [
  'INVOICE',
  'CORRECTIVE_INVOICE',
  'DEPOSIT_INVOICE',
  'TAX_DOCUMENT',
  'CORRECTIVE_TAX_DOCUMENT',
] as const;

// Fixed widths in design-system units for the label and switch columns; the four select
// columns share the remaining width equally, so switches stop stretching on wide screens.
const PAYMENT_METHOD_COLUMN_WIDTH = 36;
const SWITCH_COLUMN_WIDTH = 34;
const OVERRIDE_COLUMN_WIDTH = 30;

function getDocumentColumns() {
  return (
    [
      {width: PAYMENT_METHOD_COLUMN_WIDTH, label: i18n.t('entity.checkout.labels.paymentMethod')},
      {width: SWITCH_COLUMN_WIDTH, label: i18n.t('entity.checkout.labels.restrictSeries')},
      {width: undefined, label: i18n.t('entity.checkout.labels.allowedSeries')},
      {width: undefined, label: i18n.t('entity.checkout.labels.defaultSeries')},
      {width: SWITCH_COLUMN_WIDTH, label: i18n.t('entity.checkout.labels.restrictTemplates')},
      {width: undefined, label: i18n.t('entity.checkout.labels.allowedTemplates')},
      {width: undefined, label: i18n.t('entity.gdpr.labels.defaultTemplate')},
      {width: OVERRIDE_COLUMN_WIDTH, label: i18n.t('entity.checkout.labels.allowOverride')},
    ] as const
  ).map((column) => ({
    width: column.width,
    element: (
      <Box padding={3}>
        <Text size="xSmall" color="secondary" align="left">
          {column.label}
        </Text>
      </Box>
    ),
  }));
}

interface Props {
  // Watched by the parent form state; a second subscription here would add no re-render signal.
  profile: CheckoutProfileFormValues;
  control: FormControl<CheckoutProfileFormValues>;
  formApi: UseFormReturn<CheckoutProfileFormValues>;
  regimeStatuses: Record<SaleRegime, TabIconVariant>;
  regime: SaleRegime;
  onRegimeChange: (regime: SaleRegime) => void;
  fieldErrors?: Record<string, string>;
  issues: CheckoutProfileIssue[];
  onSelectIssue: (issue: CheckoutProfileIssue) => void;
}

export function CheckoutProfileDocuments(props: Props) {
  const profile = props.profile;

  const {
    data: series,
    isLoading: seriesLoading,
    isError: seriesError,
  } = useGetSeriesListQuery({type: ['accounting/invoice']});

  const {
    data: definitions,
    isLoading: templatesLoading,
    isError: templatesError,
  } = useGetDefinitionTemplateListQuery({});

  const seriesOptions = (series ?? []).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const regimes = checkoutProfileOptions
    .regimes()
    .filter(
      (option) =>
        option.value === 'DOMESTIC' ||
        (option.value === 'IN_EUROPE'
          ? profile.saleRegimes?.inEurope
          : profile.saleRegimes?.outOfEurope)
    );

  const regime = regimes.some((option) => option.value === props.regime)
    ? props.regime
    : 'DOMESTIC';

  const cells = getCheckoutProfileDocumentCells(normalizeCheckoutProfileDraft(profile)).filter(
    (cell) => cell.saleRegime === null || cell.saleRegime === regime
  );

  const labels = checkoutProfileOptions.documents();
  const paymentOptions = checkoutProfileOptions.payments();
  const isSubmitting = props.formApi.formState.isSubmitting;
  const applicableDocumentTypes = getCheckoutProfileDocumentTypes(profile);
  const allowDocumentLabels = {
    INVOICE: i18n.t('entity.checkout.labels.allowInvoice'),
    CORRECTIVE_INVOICE: i18n.t('entity.checkout.labels.allowCorrectiveInvoice'),
    TAX_DOCUMENT: i18n.t('entity.checkout.labels.allowTaxDocument'),
    CORRECTIVE_TAX_DOCUMENT: i18n.t('entity.checkout.labels.allowCorrectiveTaxDocument'),
    DEPOSIT_INVOICE: i18n.t('entity.checkout.labels.allowDepositInvoice'),
  };

  const columns = getDocumentColumns();
  // Deposit invoice, tax document and corrective tax document only exist for deposits, so their
  // switches are hidden, not disabled, until deposits are allowed in General. The grid keeps its
  // five columns so Invoice and Corrective invoice stay in the same place in both states.
  const hasDepositDocuments =
    profile.typeOfSale !== 'INTERNAL' && profile.deposit?.isAllowed === true;

  return (
    <VStack>
      <Heading size={4}>{i18n.t('entity.checkout.labels.documentTypes')}</Heading>
      <Space vertical={1} />
      <Text size="small" color="secondary">
        {i18n.t('entity.checkout.labels.documentSettingsDescription')}
      </Text>
      <Space vertical={4} />
      <Card variant="inlineGrey">
        <Grid columns={5} spacing={4} align="center">
          <GridItem>
            <HStack align="center" spacing={3}>
              <Switch value isDisabled aria-label={allowDocumentLabels.INVOICE} />
              <Heading size={4}>{allowDocumentLabels.INVOICE}</Heading>
            </HStack>
          </GridItem>
          <GridItem>
            <HStack align="center" spacing={3}>
              <FormField
                control={props.control}
                name="documentTypes.correctiveInvoice"
                type="switch"
                aria-label={allowDocumentLabels.CORRECTIVE_INVOICE}
              />
              <Heading size={4}>{allowDocumentLabels.CORRECTIVE_INVOICE}</Heading>
            </HStack>
          </GridItem>
          <Show when={hasDepositDocuments}>
            <GridItem>
              <HStack align="center" spacing={3}>
                <Switch
                  value={applicableDocumentTypes.includes('DEPOSIT_INVOICE')}
                  isDisabled
                  aria-label={allowDocumentLabels.DEPOSIT_INVOICE}
                />
                <Heading size={4}>{allowDocumentLabels.DEPOSIT_INVOICE}</Heading>
              </HStack>
            </GridItem>
            <GridItem>
              <HStack align="center" spacing={3}>
                <Switch
                  value={applicableDocumentTypes.includes('TAX_DOCUMENT')}
                  isDisabled
                  aria-label={allowDocumentLabels.TAX_DOCUMENT}
                />
                <Heading size={4}>{allowDocumentLabels.TAX_DOCUMENT}</Heading>
              </HStack>
            </GridItem>
            <GridItem>
              <HStack align="center" spacing={3}>
                <FormField
                  control={props.control}
                  name="documentTypes.correctiveTaxDocument"
                  type="switch"
                  aria-label={allowDocumentLabels.CORRECTIVE_TAX_DOCUMENT}
                  isDisabled={!applicableDocumentTypes.includes('TAX_DOCUMENT')}
                />
                <Heading size={4}>{allowDocumentLabels.CORRECTIVE_TAX_DOCUMENT}</Heading>
              </HStack>
            </GridItem>
          </Show>
        </Grid>
      </Card>
      <Show when={profile.typeOfSale !== 'INTERNAL'}>
        <Separator />
        <Heading size={4}>{i18n.t('entity.checkout.labels.saleRegimeSettings')}</Heading>
        <Space vertical={1} />
        <Text size="small" color="secondary">
          {i18n.t('entity.checkout.labels.documentSettingsRegimeDescription')}
        </Text>
        <Space vertical={4} />
        <TabsHeader
          activeTabId={regime}
          onChange={(value) => {
            if (value === 'DOMESTIC' || value === 'IN_EUROPE' || value === 'OUT_OF_EUROPE') {
              props.onRegimeChange(value);
            }
          }}
          tabs={regimes.map((item) => ({
            id: item.value,
            title: item.label,
            icon: props.regimeStatuses[item.value],
          }))}
        />
        <Show when={regimes.length > 1}>
          <CheckoutProfileDocumentCopy
            regime={regime}
            regimes={regimes}
            isDisabled={isSubmitting}
            onCopy={(source) => {
              props.formApi.setValue(
                'documents',
                copyCheckoutProfileRegime(props.formApi.getValues(), source, regime).documents,
                {shouldDirty: true, shouldValidate: true}
              );
            }}
          />
        </Show>
      </Show>
      <CheckoutProfileFeedback
        issues={props.issues.filter((issue) => !issue.documentType && issue.saleRegime === regime)}
        isDirty={false}
        showDocumentContext={false}
        onSelect={props.onSelectIssue}
      />
      <Show when={seriesError || templatesError}>
        <Space vertical={4} />
        <Alert variant="error" title={i18n.t('general.notifications.changesSavingFailed')} />
      </Show>
      {documentTypeOrder
        .filter((type) => applicableDocumentTypes.includes(type))
        .map((documentType) => {
          const documentCells = cells.filter((cell) => cell.documentType === documentType);

          if (documentCells.length === 0) {
            return null;
          }

          const templates = getCheckoutProfileTemplateOptions(
            definitions?.definitions ?? [],
            profile.typeOfSale,
            documentType
          );

          return (
            <VStack key={documentType}>
              <Separator />
              <section aria-label={labels[documentType]}>
                <Heading size={4}>{labels[documentType]}</Heading>
                <Space vertical={4} />
                <CheckoutProfileFeedback
                  issues={props.issues.filter(
                    (issue) =>
                      issue.documentType === documentType &&
                      (!issue.saleRegime || issue.saleRegime === regime)
                  )}
                  isDirty={false}
                  showDocumentContext={false}
                  onSelect={props.onSelectIssue}
                />
                <Card variant="inlineGrey">
                  <Box overflowX="auto">
                    <Box minWidth={320}>
                      <Table columns={columns} tableLayout="fixed">
                        {documentCells.map((cell) => {
                          const key = getCheckoutProfileDocumentCellKey(cell);
                          const index = profile.documents.findIndex(
                            (row) => getCheckoutProfileDocumentCellKey(row) === key
                          );

                          const row = profile.documents[index] ?? {
                            ...cell,
                            allowedSeriesIds: [],
                            allowedTemplateIds: [],
                            isOverrideAllowed: false,
                          };

                          const payment = paymentOptions.find(
                            (option) => option.value === cell.paymentMethod
                          );

                          return (
                            <CheckoutProfileDocumentRow
                              key={key}
                              row={row}
                              paymentLabel={
                                payment?.label ??
                                i18n.t('entity.invoice.paymentMethod.freeOfCharge')
                              }
                              series={seriesOptions}
                              templates={templates}
                              isLoading={seriesLoading || templatesLoading}
                              isDisabled={isSubmitting || seriesError || templatesError}
                              errors={Object.fromEntries(
                                (
                                  [
                                    'allowedSeriesIds',
                                    'defaultSeriesId',
                                    'allowedTemplateIds',
                                    'defaultTemplateId',
                                  ] as const
                                ).map((field) => {
                                  const serverMessage =
                                    props.fieldErrors?.[`documents.${index}.${field}`];

                                  const local = props.formApi.getFieldState(
                                    `documents.${index}.${field}`
                                  ).error;

                                  return [field, serverMessage ? {message: serverMessage} : local];
                                })
                              )}
                              onChange={(nextRow) => {
                                const current = props.formApi.getValues('documents');
                                const next = current.filter(
                                  (item) => getCheckoutProfileDocumentCellKey(item) !== key
                                );

                                props.formApi.setValue('documents', [...next, nextRow], {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }}
                            />
                          );
                        })}
                      </Table>
                    </Box>
                  </Box>
                </Card>
              </section>
            </VStack>
          );
        })}
    </VStack>
  );
}
